import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const eur = (c) => `€${(c / 100).toLocaleString("en-US")}`;
const TABS = ["Overview", "Fragrances", "Pre-Orders", "Orders", "Messages", "Settings"];

export default function Admin() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [messages, setMessages] = useState([]);
  const [preorders, setPreorders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const isAdmin = user && user.role === "admin";

  const loadAll = async () => {
    const [s, p, o, m, pre, st] = await Promise.allSettled([
      api.get("/admin/stats"),
      api.get("/products"),
      api.get("/admin/orders"),
      api.get("/admin/messages"),
      api.get("/admin/preorders"),
      api.get("/settings"),
    ]);
    if (s.status === "fulfilled") setStats(s.value.data);
    if (p.status === "fulfilled") setProducts(p.value.data);
    if (o.status === "fulfilled") setOrders(o.value.data);
    if (m.status === "fulfilled") setMessages(m.value.data);
    if (pre.status === "fulfilled") setPreorders(pre.value.data);
    if (st.status === "fulfilled") setSettings(st.value.data);
  };

  useEffect(() => {
    if (isAdmin) loadAll();
  }, [isAdmin]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(creds.email, creds.password);
      if (u.role !== "admin") {
        toast.error("This account is not an administrator.");
        navigate("/account");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (user === null)
    return <div className="pt-40 container-lux label-mono text-mineral">Loading…</div>;

  if (!isAdmin) {
    return (
      <div className="pt-32 md:pt-40 pb-24 container-lux max-w-md" data-testid="admin-login">
        <p className="label-mono mb-6">Restricted</p>
        <h1 className="heading-serif text-4xl tracking-wider mb-10">Admin Access</h1>
        <form onSubmit={doLogin} className="space-y-7">
          <input
            required
            type="email"
            placeholder="Admin email"
            value={creds.email}
            onChange={(e) => setCreds({ ...creds, email: e.target.value })}
            data-testid="admin-email-input"
            className="w-full bg-transparent border-b border-mineral/40 py-3 focus:outline-none focus:border-amethyst"
          />
          <input
            required
            type="password"
            placeholder="Password"
            value={creds.password}
            onChange={(e) => setCreds({ ...creds, password: e.target.value })}
            data-testid="admin-password-input"
            className="w-full bg-transparent border-b border-mineral/40 py-3 focus:outline-none focus:border-amethyst"
          />
          <button
            disabled={loading}
            data-testid="admin-login-btn"
            className="btn-relic w-full border-alabaster bg-alabaster text-charcoal hover:bg-amethyst hover:border-amethyst disabled:opacity-50"
          >
            {loading ? "…" : "Enter Dashboard"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="pt-28 md:pt-32 pb-24 container-lux" data-testid="admin-dashboard">
      <div className="flex flex-wrap justify-between items-end gap-6 mb-12 border-b border-mineral/20 pb-8">
        <div>
          <p className="label-mono mb-3">Beautifully Disturbed</p>
          <h1 className="heading-serif text-4xl md:text-5xl tracking-wider">Dashboard</h1>
        </div>
        <button
          onClick={logout}
          data-testid="admin-logout"
          className="btn-relic border-mineral/40 text-alabaster hover:border-amethyst hover:text-amethyst"
        >
          Sign Out
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-12">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`admin-tab-${t.toLowerCase()}`}
            className={`label-mono px-5 py-2.5 border transition-all ${
              tab === t ? "border-amethyst text-amethyst" : "border-mineral/30 text-alabaster/70 hover:border-alabaster"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-mineral/20" data-testid="admin-overview">
          <Stat label="Revenue" value={eur(stats.revenue_cents)} />
          <Stat label="Paid Orders" value={stats.paid_orders} />
          <Stat label="Customers" value={stats.customers} />
          <Stat label="Subscribers" value={stats.subscribers} />
          <Stat label="Total Orders" value={stats.total_orders} />
          <Stat label="Top Fragrance" value={stats.top_relic || "—"} wide />
        </div>
      )}

      {tab === "Fragrances" && (
        <div className="space-y-4" data-testid="admin-relics">
          {products.map((p) => (
            <RelicRow key={p.slug} product={p} onSaved={loadAll} />
          ))}
        </div>
      )}

      {tab === "Orders" && (
        <div className="space-y-3" data-testid="admin-orders">
          {orders.length === 0 && <p className="text-alabaster/60">No orders yet.</p>}
          {orders.map((o) => (
            <div key={o.session_id} className="border border-mineral/20 p-5 flex flex-wrap justify-between gap-4">
              <div>
                <p className="font-mono text-xs text-bronze mb-2">№ {o.session_id.slice(-8).toUpperCase()}</p>
                <p className="font-serif text-lg">
                  {o.items?.map((i) => `${i.name} ×${i.quantity}`).join(" · ")}
                </p>
                <p className="label-mono text-mineral mt-1">{o.email || "guest"}</p>
              </div>
              <div className="text-right">
                <p className="font-serif text-xl">{eur(o.amount)}</p>
                <p
                  className={`label-mono mt-1 ${
                    o.payment_status === "paid" ? "text-amethyst" : "text-mineral"
                  }`}
                >
                  {o.payment_status}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Pre-Orders" && (
        <div className="space-y-3" data-testid="admin-preorders">
          {preorders.length === 0 && <p className="text-alabaster/60">No pre-orders yet.</p>}
          {preorders.map((p) => (
            <div key={p.reference} className="border border-mineral/20 p-5 flex flex-wrap justify-between gap-4">
              <div>
                <p className="font-mono text-xs text-blood mb-2">{p.reference}</p>
                <p className="font-serif text-lg">
                  {p.items?.map((i) => `${i.name} ×${i.quantity}`).join(" · ")}
                </p>
                <p className="label-mono text-mineral mt-1">
                  {p.customer?.full_name} · {p.email} · {p.customer?.city}, {p.customer?.country}
                </p>
              </div>
              <div className="text-right">
                <p className="font-serif text-xl">€{((p.amount || 0) / 100).toLocaleString("en-US")}</p>
                <p className="label-mono mt-1 text-blood">{p.payment_mode} · {p.payment_status}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "Settings" && settings && (
        <SettingsPanel settings={settings} onSaved={loadAll} />
      )}

      {tab === "Messages" && (
        <div className="space-y-3" data-testid="admin-messages">
          {messages.length === 0 && <p className="text-alabaster/60">No messages yet.</p>}
          {messages.map((m, i) => (
            <div key={i} className="border border-mineral/20 p-5">
              <div className="flex justify-between mb-2">
                <span className="font-serif text-lg">{m.name}</span>
                <span className="label-mono text-amethyst">{m.subject}</span>
              </div>
              <p className="label-mono text-mineral mb-3">{m.email}</p>
              <p className="text-sm text-alabaster/75 leading-relaxed">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, wide }) {
  return (
    <div className={`bg-charcoal p-8 ${wide ? "lg:col-span-2" : ""}`}>
      <p className="label-mono mb-4">{label}</p>
      <p className="heading-serif text-3xl md:text-4xl">{value}</p>
    </div>
  );
}

function RelicRow({ product, onSaved }) {
  const [price, setPrice] = useState(product.price);
  const [stock, setStock] = useState(product.stock);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/products/${product.slug}`, {
        price: Number(price),
        stock: Number(stock),
      });
      toast.success(`${product.name} updated`);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border border-mineral/20 p-5 flex flex-wrap items-center gap-6"
      data-testid={`admin-relic-${product.slug}`}
    >
      <img src={product.image} alt="" className="w-14 h-16 object-cover" />
      <div className="flex-1 min-w-[140px]">
        <p className="font-serif text-lg">{product.name}</p>
        <p className="label-mono text-mineral mt-1">{product.olfactory_family}</p>
      </div>
      <label className="label-mono flex items-center gap-2">
        € Price
        <input
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          data-testid={`admin-price-${product.slug}`}
          className="w-24 bg-transparent border-b border-mineral/40 py-1 text-alabaster focus:outline-none focus:border-amethyst"
        />
      </label>
      <label className="label-mono flex items-center gap-2">
        Stock
        <input
          type="number"
          value={stock}
          onChange={(e) => setStock(e.target.value)}
          data-testid={`admin-stock-${product.slug}`}
          className="w-20 bg-transparent border-b border-mineral/40 py-1 text-alabaster focus:outline-none focus:border-amethyst"
        />
      </label>
      <button
        onClick={save}
        disabled={saving}
        data-testid={`admin-save-${product.slug}`}
        className="btn-relic border-amethyst text-amethyst hover:bg-amethyst hover:text-charcoal disabled:opacity-50"
      >
        {saving ? "…" : "Save"}
      </button>
    </div>
  );
}


function SettingsPanel({ settings, onSaved }) {
  const [mode, setMode] = useState(settings.preorder_payment_mode);
  const [est, setEst] = useState(settings.preorder_estimated_shipping || "");
  const [ship, setShip] = useState(settings.shipping_cost || 0);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        preorder_payment_mode: mode,
        preorder_estimated_shipping: est,
        shipping_cost: Number(ship),
      });
      toast.success("Settings saved");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl space-y-8" data-testid="admin-settings">
      <div>
        <p className="label-mono mb-4">Pre-order payment mode</p>
        <div className="flex gap-3">
          {[
            { v: "payment", l: "With payment" },
            { v: "noPayment", l: "Without payment" },
          ].map((o) => (
            <button
              key={o.v}
              onClick={() => setMode(o.v)}
              data-testid={`settings-mode-${o.v}`}
              className={`btn-relic border ${mode === o.v ? "border-blood text-blood" : "border-mineral/30 text-alabaster/70 hover:border-alabaster"}`}
            >
              {o.l}
            </button>
          ))}
        </div>
        <p className="label-mono text-mineral mt-3">
          {mode === "payment" ? "Customers reserve by paying at secure checkout." : "Customers register and receive payment instructions later."}
        </p>
      </div>

      <label className="block">
        <span className="label-mono block mb-2">Estimated shipping (optional, shown in emails)</span>
        <input
          value={est}
          onChange={(e) => setEst(e.target.value)}
          placeholder="e.g. Spring 2026 — leave blank if unknown"
          data-testid="settings-estimated"
          className="w-full bg-transparent border-b border-mineral/40 py-2 focus:outline-none focus:border-blood"
        />
      </label>

      <label className="block max-w-[200px]">
        <span className="label-mono block mb-2">Flat shipping cost (€)</span>
        <input
          type="number"
          value={ship}
          onChange={(e) => setShip(e.target.value)}
          data-testid="settings-shipping"
          className="w-full bg-transparent border-b border-mineral/40 py-2 focus:outline-none focus:border-blood"
        />
      </label>

      <button
        onClick={save}
        disabled={saving}
        data-testid="settings-save"
        className="btn-relic border-blood bg-blood text-ivory hover:bg-ivory hover:border-ivory hover:text-ink disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save Settings"}
      </button>
    </div>
  );
}
